import React, { Component, createRef } from 'react';
import Link from 'next/link';
import Router from 'next/router';
import styles from './search-box.module.scss';

const SEARCH_API = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4030/v1/phoenix/api/unit-converter/search'
    : 'https://ncf-api-v1.xconvert.com/v1/phoenix/api/unit-converter/search';

const POPULAR_API = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4030/v1/phoenix/api/unit-converter/popular'
    : 'https://ncf-api-v1.xconvert.com/v1/phoenix/api/unit-converter/popular';

export const VIEW_API = typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? 'http://localhost:4030/v1/phoenix/api/unit-converter/view'
    : 'https://ncf-api-v1.xconvert.com/v1/phoenix/api/unit-converter/view';

interface SearchResult {
    id: string;
    title: string;
    abbrTitle: string;
    fromPlural: string;
    toPlural: string;
    fromUnit: string;
    toUnit: string;
    measure: string;
    measureReadable: string;
    url: string;
}

interface SearchBoxState {
    query: string;
    results: SearchResult[];
    isOpen: boolean;
    highlightIndex: number;
    loading: boolean;
}

const MEASURE_COLORS: { [key: string]: string } = {
    temperature: '#e25950', length: '#3ecf8e', mass: '#f5a623',
    digital: '#5469d4', dataTransferRate: '#5469d4', time: '#8b5cf6',
    speed: '#06b6d4', volume: '#ec4899', area: '#84cc16',
    energy: '#f97316', pressure: '#6366f1', frequency: '#14b8a6',
};

interface SearchBoxProps {
    initialQuery?: string;
}

export default class SearchBox extends Component<SearchBoxProps, SearchBoxState> {
    private inputRef = createRef<HTMLInputElement>();
    private debounceTimer: any = null;
    private wrapperRef = createRef<HTMLDivElement>();

    state: SearchBoxState = { query: '', results: [], isOpen: false, highlightIndex: -1, loading: false };

    componentDidMount() {
        document.addEventListener('mousedown', this.handleClickOutside);
        if (this.props.initialQuery) {
            this.setState({ query: this.props.initialQuery, isOpen: true });
            this.fetchSearch(this.props.initialQuery);
        }
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClickOutside);
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
    }

    handleClickOutside = (e: MouseEvent) => {
        if (this.wrapperRef.current && !this.wrapperRef.current.contains(e.target as Node)) {
            this.setState({ isOpen: false });
        }
    };

    onFocus = () => {
        if (this.state.query.length < 2 && this.state.results.length === 0) {
            this.fetchPopular();
        }
        this.setState({ isOpen: true });
    };

    onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        this.setState({ query, highlightIndex: -1 });

        if (this.debounceTimer) clearTimeout(this.debounceTimer);

        if (query.length < 2) {
            this.fetchPopular();
            return;
        }

        this.debounceTimer = setTimeout(() => this.fetchSearch(query), 200);
    };

    onKeyDown = (e: React.KeyboardEvent) => {
        const { results, highlightIndex } = this.state;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            this.setState({ highlightIndex: Math.min(highlightIndex + 1, results.length - 1) });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            this.setState({ highlightIndex: Math.max(highlightIndex - 1, -1) });
        } else if (e.key === 'Enter' && highlightIndex >= 0 && results[highlightIndex]) {
            e.preventDefault();
            this.navigate(results[highlightIndex]);
        } else if (e.key === 'Escape') {
            this.setState({ isOpen: false });
        }
    };

    navigate(result: SearchResult) {
        this.setState({ isOpen: false, query: '' });
        Router.push(result.url.replace('/unit-converter', ''));
    }

    highlightMatch(text: string, query: string): string {
        if (!query || query.length < 2) return text;
        const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
    }

    async fetchSearch(query: string) {
        this.setState({ loading: true });
        try {
            const res = await fetch(`${SEARCH_API}?q=${encodeURIComponent(query)}&limit=8`);
            const data = await res.json();
            this.setState({ results: data, isOpen: true, loading: false });
        } catch { this.setState({ loading: false }); }
    }

    async fetchPopular() {
        try {
            const res = await fetch(`${POPULAR_API}?limit=8`);
            const data = await res.json();
            this.setState({ results: data, isOpen: true });
        } catch {}
    }

    render() {
        const { query, results, isOpen, highlightIndex, loading } = this.state;
        return (
            <div className={styles.wrapper} ref={this.wrapperRef}>
                <div className={styles.inputWrapper}>
                    <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
                    </svg>
                    <input
                        ref={this.inputRef}
                        type="text"
                        className={styles.input}
                        placeholder="Search conversions..."
                        value={query}
                        onChange={this.onChange}
                        onFocus={this.onFocus}
                        onKeyDown={this.onKeyDown}
                        autoComplete="off"
                    />
                </div>
                {isOpen && (
                    <div className={styles.dropdown}>
                        {query.length < 2 && results.length > 0 && <div className={styles.dropdownLabel}>Popular conversions</div>}
                        {results.map((r, i) => (
                            <div
                                key={r.id}
                                className={`${styles.resultRow} ${i === highlightIndex ? styles.highlighted : ''}`}
                                onMouseEnter={() => this.setState({ highlightIndex: i })}
                                onClick={() => this.navigate(r)}
                            >
                                <div className={styles.resultMain}>
                                    <span className={styles.resultTitle} dangerouslySetInnerHTML={{ __html: this.highlightMatch(r.title, query) }}></span>
                                    <span className={styles.resultAbbr}>{r.fromUnit} → {r.toUnit}</span>
                                </div>
                                <span
                                    className={styles.measureBadge}
                                    style={{ backgroundColor: (MEASURE_COLORS[r.measure] || '#8898aa') + '18', color: MEASURE_COLORS[r.measure] || '#8898aa' }}
                                >
                                    {r.measureReadable}
                                </span>
                            </div>
                        ))}
                        {results.length === 0 && query.length >= 2 && !loading && (
                            <div className={styles.emptyState}>No results for "{query}"</div>
                        )}
                    </div>
                )}
            </div>
        );
    }
}
